// @ts-nocheck
/**
 * UserStickyPillDock Component
 *
 * Bottom-right container for collapsed user sticky pills and menu button.
 * Click pill to restore panel with spring animation.
 * Respects safe-area insets for notched devices.
 *
 * Features:
 * - Desktop: Horizontal pill row with menu button + chevron (3 states: full/menuOnly/hidden)
 * - Mobile: Compact FAB with badge + slide-up drawer for stickies list
 * - Animated pill buttons
 * - Truncated titles (can be hidden in menuOnly mode)
 * - Color-coded pills matching sticky colors
 * - Menu button opens drawer with notes list, New Note, and Show Titles options
 */

import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { StickyNote, ChevronLeft, ChevronRight, ChevronDown, Plus, X, Eye, EyeOff } from 'lucide-react';
import type { UserStickyNote } from '@shared/types/userStickies';
import { stickyColorSchemes, dynamicsColorScheme } from '@shared/types/userStickies';
import { useIsMobile } from '@shared/hooks/useIsMobile';
import { useUIStore } from '@shared/store/uiStore';
import type { DockDisplayMode } from '@shared/store/useUserStickiesStore';

interface UserStickyPillProps {
  sticky: UserStickyNote;
  onClick: () => void;
  showTitle?: boolean;  // Whether to show title text (default: true)
}

interface UserStickyPillDockProps {
  collapsedStickies: UserStickyNote[];
  onRestore: (id: string) => void;
  onCreateNew: () => void;
  dockDisplayMode: DockDisplayMode;
  onCycleDockDisplayMode: () => void;
  onSetDockDisplayMode: (mode: DockDisplayMode) => void;
  // Legacy props for backwards compatibility
  isDockCollapsed?: boolean;
  onToggleDockCollapsed?: () => void;
}

/**
 * Animation variants for pill
 */
const pillVariants = {
  hidden: { opacity: 0, scale: 0, x: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    x: 0,
    transition: { type: 'spring', stiffness: 400, damping: 25 },
  },
  exit: { opacity: 0, scale: 0, x: 20, transition: { duration: 0.15 } },
  hover: { scale: 1.05 },
  tap: { scale: 0.95 },
};

/**
 * Reduced motion variants
 */
const reducedMotionVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.1 } },
  exit: { opacity: 0, transition: { duration: 0.1 } },
  hover: {},
  tap: {},
};

/**
 * Individual minimized pill button
 */
const UserStickyPill: React.FC<UserStickyPillProps> = ({ sticky, onClick, showTitle = true }) => {
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();
  const themeClass = useUIStore((state) => state.themeClass);
  const variants = prefersReducedMotion ? reducedMotionVariants : pillVariants;

  // Use Dynamics theme color scheme (professional grey) when active
  const isDynamicsTheme = themeClass === 'theme-dynamics';
  const colorScheme = useMemo(() => {
    return isDynamicsTheme ? dynamicsColorScheme : stickyColorSchemes[sticky.color];
  }, [isDynamicsTheme, sticky.color]);

  // Truncate title for pill display
  const displayTitle = sticky.title.length > 10
    ? `${sticky.title.substring(0, 10)}...`
    : sticky.title;

  return (
    <motion.button
      variants={variants}
      initial="hidden"
      animate="visible"
      exit="exit"
      whileHover="hover"
      whileTap="tap"
      onClick={onClick}
      className={`
        ${colorScheme.pillBg} ${colorScheme.pillBorder}
        border-2 rounded-full shadow-md
        flex items-center gap-2
        ${showTitle ? 'px-4 py-2' : 'p-2'}
        cursor-pointer
        focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2
        transition-shadow hover:shadow-lg
        ${isMobile ? 'min-h-[44px]' : ''}
      `}
      aria-label={`Restore "${sticky.title}"`}
      aria-expanded={false}
      data-testid={`user-sticky-pill-${sticky.id}`}
    >
      <StickyNote size={16} className={colorScheme.iconColor} aria-hidden="true" />
      {showTitle && (
        <span className={`text-sm font-medium whitespace-nowrap ${colorScheme.headerText}`}>
          {displayTitle}
        </span>
      )}
    </motion.button>
  );
};

/**
 * Animation variants for collapsed ribbon
 */
const ribbonVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { type: 'spring', stiffness: 400, damping: 25 },
  },
  exit: { opacity: 0, x: 20, transition: { duration: 0.15 } },
};

/**
 * Animation variants for dock expand
 */
const dockVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { type: 'spring', stiffness: 400, damping: 25 },
  },
  exit: { opacity: 0, scale: 0.8, transition: { duration: 0.15 } },
};

/**
 * Collapsed ribbon button - narrow vertical strip to expand dock
 */
const CollapsedRibbon: React.FC<{
  onClick: () => void;
  stickyCount: number;
}> = ({ onClick, stickyCount }) => {
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.button
      variants={prefersReducedMotion ? { hidden: { opacity: 0 }, visible: { opacity: 1 }, exit: { opacity: 0 } } : ribbonVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`
        bg-brand-500 hover:bg-brand-600
        text-white
        shadow-lg hover:shadow-xl
        rounded-l-lg rounded-r-none
        flex flex-col items-center justify-center gap-1
        cursor-pointer
        focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2
        transition-all duration-200
        ${isMobile ? 'w-8 min-h-[80px] py-3' : 'w-6 min-h-[64px] py-2'}
      `}
      aria-label="Expand sticky notes dock"
      aria-expanded={false}
      data-testid="sticky-dock-ribbon"
    >
      <ChevronLeft size={isMobile ? 18 : 14} aria-hidden="true" />
      <StickyNote size={isMobile ? 16 : 14} aria-hidden="true" />
      {stickyCount > 0 && (
        <span className={`font-medium ${isMobile ? 'text-xs' : 'text-[10px]'}`}>
          {stickyCount}
        </span>
      )}
    </motion.button>
  );
};

/**
 * Mobile FAB - smaller size (44px) to match Focus/Goals pills
 */
const MobileStickyFAB: React.FC<{
  stickyCount: number;
  isDrawerOpen: boolean;
  onClick: () => void;
}> = ({ stickyCount, isDrawerOpen, onClick }) => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.button
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`
        relative
        bg-brand-500 hover:bg-brand-600
        text-white shadow-lg
        rounded-full
        w-11 h-11 min-w-[44px] min-h-[44px]
        flex items-center justify-center
        cursor-pointer
        focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2
        transition-colors duration-200
      `}
      aria-label={isDrawerOpen ? 'Close sticky notes' : `Open sticky notes (${stickyCount} minimized)`}
      aria-expanded={isDrawerOpen}
      data-testid="mobile-sticky-fab"
    >
      <AnimatePresence mode="wait">
        {isDrawerOpen ? (
          <motion.span
            key="close"
            initial={prefersReducedMotion ? { opacity: 0 } : { rotate: -90, opacity: 0 }}
            animate={prefersReducedMotion ? { opacity: 1 } : { rotate: 0, opacity: 1 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { rotate: 90, opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <X size={18} strokeWidth={2.5} />
          </motion.span>
        ) : (
          <motion.span
            key="sticky"
            initial={prefersReducedMotion ? { opacity: 0 } : { rotate: 90, opacity: 0 }}
            animate={prefersReducedMotion ? { opacity: 1 } : { rotate: 0, opacity: 1 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { rotate: -90, opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <StickyNote size={18} />
          </motion.span>
        )}
      </AnimatePresence>

      {/* Badge - only show when drawer is closed and has minimized stickies */}
      {!isDrawerOpen && stickyCount > 0 && (
        <span
          className="
            absolute -top-1 -right-1
            bg-red-500 text-white
            text-xs font-bold
            w-5 h-5 min-w-[20px]
            rounded-full
            flex items-center justify-center
            shadow-sm
          "
          aria-hidden="true"
        >
          {stickyCount > 9 ? '9+' : stickyCount}
        </span>
      )}
    </motion.button>
  );
};

/**
 * Sticky drawer - vertical list of minimized stickies with menu options
 * Used by both mobile and desktop (in menuOnly mode)
 */
const StickyDrawer: React.FC<{
  collapsedStickies: UserStickyNote[];
  onRestore: (id: string) => void;
  onCreateNew: () => void;
  onClose: () => void;
  showTitlesOption?: boolean;  // Show "Show Titles" toggle (desktop only)
  titlesVisible?: boolean;  // Current state of titles visibility
  onToggleTitles?: () => void;  // Toggle titles visibility
}> = ({ collapsedStickies, onRestore, onCreateNew, onClose, showTitlesOption = false, titlesVisible = true, onToggleTitles }) => {
  const prefersReducedMotion = useReducedMotion();
  const themeClass = useUIStore((state) => state.themeClass);
  const isDynamicsTheme = themeClass === 'theme-dynamics';

  const drawerVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: prefersReducedMotion
        ? { duration: 0.1 }
        : { type: 'spring', stiffness: 400, damping: 30 },
    },
    exit: {
      opacity: 0,
      y: 20,
      scale: 0.95,
      transition: { duration: 0.15 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: prefersReducedMotion
        ? { duration: 0.1 }
        : { delay: i * 0.05, type: 'spring', stiffness: 400, damping: 25 },
    }),
  };

  return (
    <motion.div
      variants={drawerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className={`
        ${isDynamicsTheme ? 'bg-dark-surface border-dark-border/30' : 'bg-dark-surface border-dark-border/30'}
        border rounded-2xl shadow-xl
        p-3
        w-64 max-w-[calc(100vw-32px)]
        max-h-[60vh]
        overflow-y-auto
      `}
      role="menu"
      aria-label="Minimized sticky notes"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3 px-1">
        <span className={`text-sm font-semibold ${isDynamicsTheme ? 'text-white' : 'text-dark-text'}`}>
          Notes ({collapsedStickies.length})
        </span>
        <button
          onClick={onClose}
          className={`
            p-1 rounded-full
            transition-all duration-150
            ${isDynamicsTheme ? 'text-dark-muted hover:text-dark-text' : 'text-dark-muted hover:text-dark-muted'}
          `}
          aria-label="Close drawer"
        >
          <ChevronDown size={18} />
        </button>
      </div>

      {/* List of minimized stickies */}
      {collapsedStickies.length > 0 ? (
        <div className="space-y-2">
          {collapsedStickies.map((sticky, index) => {
            const colorScheme = isDynamicsTheme
              ? dynamicsColorScheme
              : stickyColorSchemes[sticky.color];

            return (
              <motion.button
                key={sticky.id}
                custom={index}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  onRestore(sticky.id);
                  onClose();
                }}
                className={`
                  w-full
                  ${colorScheme.pillBg} ${colorScheme.pillBorder}
                  border rounded-xl
                  flex items-center gap-3
                  px-3 py-3
                  min-h-[48px]
                  cursor-pointer
                  focus:outline-none focus:ring-2 focus:ring-brand-400
                  transition-shadow hover:shadow-md
                  text-left
                `}
                role="menuitem"
                aria-label={`Restore "${sticky.title}"`}
              >
                <StickyNote size={18} className={colorScheme.iconColor} aria-hidden="true" />
                <div className="flex-1 min-w-0">
                  <span className={`block text-sm font-medium truncate ${colorScheme.headerText}`}>
                    {sticky.title || 'Untitled'}
                  </span>
                  {sticky.content && (
                    <span className={`block text-xs truncate mt-0.5 ${isDynamicsTheme ? 'text-dark-muted' : 'text-dark-muted'}`}>
                      {sticky.content.slice(0, 40)}
                      {sticky.content.length > 40 ? '...' : ''}
                    </span>
                  )}
                </div>
              </motion.button>
            );
          })}
        </div>
      ) : (
        <p className={`text-sm text-center py-4 ${isDynamicsTheme ? 'text-dark-muted' : 'text-dark-muted'}`}>
          No minimized notes
        </p>
      )}

      {/* Create new button */}
      <motion.button
        variants={itemVariants}
        custom={collapsedStickies.length}
        initial="hidden"
        animate="visible"
        whileTap={{ scale: 0.98 }}
        onClick={() => {
          onCreateNew();
          onClose();
        }}
        className="
          w-full mt-3
          bg-brand-500 hover:bg-brand-600
          text-white
          rounded-xl
          flex items-center justify-center gap-2
          px-3 py-3
          min-h-[48px]
          cursor-pointer
          focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2
          transition-all duration-150
        "
        role="menuitem"
        aria-label="Create new sticky note"
      >
        <Plus size={18} strokeWidth={2.5} />
        <span className="text-sm font-medium">New Note</span>
      </motion.button>

      {/* Show Titles toggle (desktop only) */}
      {showTitlesOption && onToggleTitles && (
        <motion.button
          variants={itemVariants}
          custom={collapsedStickies.length + 1}
          initial="hidden"
          animate="visible"
          whileTap={{ scale: 0.98 }}
          onClick={() => {
            onToggleTitles();
            onClose();
          }}
          className={`
            w-full mt-2
            ${isDynamicsTheme ? 'bg-dark-input hover:bg-dark-surface/40 text-dark-text' : 'bg-dark-input hover:bg-dark-surface/40 text-dark-text'}
            rounded-xl
            flex items-center justify-center gap-2
            px-3 py-3
            min-h-[48px]
            cursor-pointer
            focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2
            transition-all duration-150
          `}
          role="menuitem"
          aria-label={titlesVisible ? 'Hide titles' : 'Show titles'}
        >
          {titlesVisible ? (
            <>
              <EyeOff size={18} />
              <span className="text-sm font-medium">Hide Titles</span>
            </>
          ) : (
            <>
              <Eye size={18} />
              <span className="text-sm font-medium">Show Titles</span>
            </>
          )}
        </motion.button>
      )}
    </motion.div>
  );
};

/**
 * Mobile-optimized dock with FAB + drawer pattern
 */
const MobileStickyDock: React.FC<{
  collapsedStickies: UserStickyNote[];
  onRestore: (id: string) => void;
  onCreateNew: () => void;
}> = ({ collapsedStickies, onRestore, onCreateNew }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  return (
    <div
      className="fixed z-50 bottom-[calc(16px+env(safe-area-inset-bottom))] right-4"
      role="group"
      aria-label="User sticky notes"
    >
      {/* Drawer (positioned above FAB) */}
      <AnimatePresence>
        {isDrawerOpen && (
          <div className="absolute bottom-16 right-0 mb-2">
            <StickyDrawer
              collapsedStickies={collapsedStickies}
              onRestore={onRestore}
              onCreateNew={onCreateNew}
              onClose={() => setIsDrawerOpen(false)}
            />
          </div>
        )}
      </AnimatePresence>

      {/* FAB */}
      <MobileStickyFAB
        stickyCount={collapsedStickies.length}
        isDrawerOpen={isDrawerOpen}
        onClick={() => setIsDrawerOpen((prev) => !prev)}
      />
    </div>
  );
};

/**
 * Desktop menu button - smaller size (44px) to match Focus/Goals pills
 */
const DesktopMenuButton: React.FC<{
  stickyCount: number;
  isDrawerOpen: boolean;
  onClick: () => void;
}> = ({ stickyCount, isDrawerOpen, onClick }) => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <motion.button
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`
        relative
        bg-brand-500 hover:bg-brand-600
        text-white shadow-lg
        rounded-full
        w-11 h-11
        flex items-center justify-center
        cursor-pointer
        focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2
        transition-colors duration-200
      `}
      aria-label={isDrawerOpen ? 'Close sticky notes menu' : `Open sticky notes menu (${stickyCount} minimized)`}
      aria-expanded={isDrawerOpen}
      data-testid="desktop-sticky-menu-btn"
    >
      <AnimatePresence mode="wait">
        {isDrawerOpen ? (
          <motion.span
            key="close"
            initial={prefersReducedMotion ? { opacity: 0 } : { rotate: -90, opacity: 0 }}
            animate={prefersReducedMotion ? { opacity: 1 } : { rotate: 0, opacity: 1 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { rotate: 90, opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <X size={18} strokeWidth={2.5} />
          </motion.span>
        ) : (
          <motion.span
            key="sticky"
            initial={prefersReducedMotion ? { opacity: 0 } : { rotate: 90, opacity: 0 }}
            animate={prefersReducedMotion ? { opacity: 1 } : { rotate: 0, opacity: 1 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { rotate: -90, opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <StickyNote size={18} />
          </motion.span>
        )}
      </AnimatePresence>

      {/* Badge - only show when drawer is closed and has minimized stickies */}
      {!isDrawerOpen && stickyCount > 0 && (
        <span
          className="
            absolute -top-1 -right-1
            bg-red-500 text-white
            text-xs font-bold
            w-5 h-5 min-w-[20px]
            rounded-full
            flex items-center justify-center
            shadow-sm
          "
          aria-hidden="true"
        >
          {stickyCount > 9 ? '9+' : stickyCount}
        </span>
      )}
    </motion.button>
  );
};

/**
 * Desktop dock container - 3 display modes
 * - 'full': Shows pills with titles + menu button + chevron
 * - 'menuOnly': Shows menu button + chevron (pills hidden)
 * - 'hidden': Shows only collapsed ribbon to restore
 */
const DesktopStickyDock: React.FC<{
  collapsedStickies: UserStickyNote[];
  onRestore: (id: string) => void;
  onCreateNew: () => void;
  dockDisplayMode: DockDisplayMode;
  onCycleDockDisplayMode: () => void;
  onSetDockDisplayMode: (mode: DockDisplayMode) => void;
}> = ({ collapsedStickies, onRestore, onCreateNew, dockDisplayMode, onCycleDockDisplayMode, onSetDockDisplayMode }) => {
  const prefersReducedMotion = useReducedMotion();
  const themeClass = useUIStore((state) => state.themeClass);
  const isDynamicsTheme = themeClass === 'theme-dynamics';
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Helper to toggle titles (switch between full and menuOnly)
  const handleToggleTitles = () => {
    if (dockDisplayMode === 'full') {
      onSetDockDisplayMode('menuOnly');
    } else {
      onSetDockDisplayMode('full');
    }
  };

  // When dock is hidden, show only the narrow vertical ribbon
  if (dockDisplayMode === 'hidden') {
    return (
      <div
        className="fixed z-50 bottom-4 right-0"
        role="group"
        aria-label="User sticky notes (hidden)"
      >
        <AnimatePresence mode="wait">
          <CollapsedRibbon
            key="ribbon"
            onClick={() => onSetDockDisplayMode('full')}
            stickyCount={collapsedStickies.length}
          />
        </AnimatePresence>
      </div>
    );
  }

  // menuOnly mode: just menu button + chevron
  if (dockDisplayMode === 'menuOnly') {
    return (
      <div
        className="fixed z-50 flex items-center gap-2 bottom-4 right-4"
        role="group"
        aria-label="User sticky notes (compact)"
      >
        {/* Chevron to cycle modes */}
        <motion.button
          variants={prefersReducedMotion ? { hidden: { opacity: 0 }, visible: { opacity: 1 } } : dockVariants}
          initial="hidden"
          animate="visible"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onCycleDockDisplayMode}
          className={`
            ${isDynamicsTheme ? 'bg-dark-surface/50 hover:bg-dark-surface/50 text-dark-muted' : 'bg-dark-surface/50 hover:bg-dark-surface/50 text-dark-muted'}
            rounded-full shadow-md hover:shadow-lg
            flex items-center justify-center
            cursor-pointer
            focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2
            transition-all duration-200
            w-8 h-8
          `}
          aria-label="Hide sticky notes dock"
          data-testid="sticky-dock-cycle-btn"
        >
          <ChevronRight size={16} aria-hidden="true" />
        </motion.button>

        {/* Menu button with drawer */}
        <div className="relative">
          <DesktopMenuButton
            stickyCount={collapsedStickies.length}
            isDrawerOpen={isDrawerOpen}
            onClick={() => setIsDrawerOpen((prev) => !prev)}
          />

          {/* Drawer (positioned above menu button) */}
          <AnimatePresence>
            {isDrawerOpen && (
              <div className="absolute bottom-14 right-0 mb-2">
                <StickyDrawer
                  collapsedStickies={collapsedStickies}
                  onRestore={onRestore}
                  onCreateNew={onCreateNew}
                  onClose={() => setIsDrawerOpen(false)}
                  showTitlesOption={true}
                  titlesVisible={false}
                  onToggleTitles={handleToggleTitles}
                />
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  }

  // full mode: pills with titles + menu button + chevron
  return (
    <div
      className="fixed z-50 flex items-center gap-2 bottom-4 right-4"
      role="group"
      aria-label="User sticky notes"
    >
      {/* Chevron to cycle modes (full -> menuOnly) */}
      <motion.button
        variants={prefersReducedMotion ? { hidden: { opacity: 0 }, visible: { opacity: 1 } } : dockVariants}
        initial="hidden"
        animate="visible"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={onCycleDockDisplayMode}
        className={`
          ${isDynamicsTheme ? 'bg-dark-surface/50 hover:bg-dark-surface/50 text-dark-muted' : 'bg-dark-surface/50 hover:bg-dark-surface/50 text-dark-muted'}
          rounded-full shadow-md hover:shadow-lg
          flex items-center justify-center
          cursor-pointer
          focus:outline-none focus:ring-2 focus:ring-brand-400 focus:ring-offset-2
          transition-all duration-200
          w-8 h-8
        `}
        aria-label="Collapse sticky notes dock"
        aria-expanded={true}
        data-testid="sticky-dock-cycle-btn"
      >
        <ChevronRight size={16} aria-hidden="true" />
      </motion.button>

      {/* Collapsed sticky pills with titles */}
      <AnimatePresence mode="popLayout">
        {collapsedStickies.map((sticky) => (
          <UserStickyPill
            key={sticky.id}
            sticky={sticky}
            onClick={() => onRestore(sticky.id)}
            showTitle={true}
          />
        ))}
      </AnimatePresence>

      {/* Menu button with drawer (replaces plus button) */}
      <div className="relative">
        <DesktopMenuButton
          stickyCount={collapsedStickies.length}
          isDrawerOpen={isDrawerOpen}
          onClick={() => setIsDrawerOpen((prev) => !prev)}
        />

        {/* Drawer (positioned above menu button) */}
        <AnimatePresence>
          {isDrawerOpen && (
            <div className="absolute bottom-14 right-0 mb-2">
              <StickyDrawer
                collapsedStickies={collapsedStickies}
                onRestore={onRestore}
                onCreateNew={onCreateNew}
                onClose={() => setIsDrawerOpen(false)}
                showTitlesOption={true}
                titlesVisible={true}
                onToggleTitles={handleToggleTitles}
              />
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

/**
 * Dock container for minimized user sticky pills and menu button
 * Mobile: Compact FAB with badge + slide-up drawer
 * Desktop: 3 display modes (full/menuOnly/hidden) with menu button + chevron
 */
export const UserStickyPillDock: React.FC<UserStickyPillDockProps> = ({
  collapsedStickies,
  onRestore,
  onCreateNew,
  dockDisplayMode,
  onCycleDockDisplayMode,
  onSetDockDisplayMode,
  // Legacy props (ignored, included for backwards compatibility)
  isDockCollapsed: _isDockCollapsed,
  onToggleDockCollapsed: _onToggleDockCollapsed,
}) => {
  const isMobile = useIsMobile();

  // Mobile uses FAB + drawer pattern (no display modes needed)
  if (isMobile) {
    return (
      <MobileStickyDock
        collapsedStickies={collapsedStickies}
        onRestore={onRestore}
        onCreateNew={onCreateNew}
      />
    );
  }

  // Desktop uses 3 display modes with menu button
  return (
    <DesktopStickyDock
      collapsedStickies={collapsedStickies}
      onRestore={onRestore}
      onCreateNew={onCreateNew}
      dockDisplayMode={dockDisplayMode}
      onCycleDockDisplayMode={onCycleDockDisplayMode}
      onSetDockDisplayMode={onSetDockDisplayMode}
    />
  );
};

